import { useState, useEffect, useRef } from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider,
  Chip, Paper, CircularProgress, Tooltip, Button,
  FormControl, InputLabel, Select, MenuItem, TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as ProcessIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Assignment as TranscriptIcon,
  PersonOff as DiscardIcon,
  RestartAlt as RestoreIcon,
  Block as BlockIcon,
  LockOpen as LockOpenIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  AutoAwesome as AiIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  LockOutlined as LockIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import DiscReportModal from '../../disc/DiscReportModal';
import ReasoningTestResultModal from '../../reasoningTests/ReasoningTestResultModal';
import PersonalHabilitiesTestResultModal from '../../personalHabilitiesTests/PersonalHabilitiesTestResultModal';
import SalesAssessmentReportModal from '../../salesAssessment/SalesAssessmentReportModal';
import AtsDiscardDialog from './AtsDiscardDialog';
import AtsRequestDocumentsDialog from './AtsRequestDocumentsDialog';
import { MODULE_CONFIG } from './AtsStageColumn';
import AtsTestSendDialog from './AtsTestSendDialog';
import AtsInterviewDialog from './AtsInterviewDialog';
import AtsPsycholaboralDialog from './AtsPsycholaboralDialog';
import AtsInterviewQuestions from './AtsInterviewQuestions';
import AtsBackgroundCheckSection from './AtsBackgroundCheckSection';
import { validationReasonText, formatValidationPhone } from './AtsValidateDialog';
import { hasBackgroundCheckStage, stageHasBackgroundCheck } from './defaultPipeline';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { useCheckPoints, pointsBlockFromError, pointsBlockMessage } from './backgroundCheckPricing';
import formatRut from '../../erp/formatRut';

const COLLECTION_MAP = {
  disc_test: 'disc_tests',
  reasoning_test: 'reasoning_tests',
  personal_skills: 'reasoning_tests',
  sales_assessment: 'sales_assessments',
};

const TEST_CONFIG = {
  disc_test: {
    labelKey: 'ats.modules.discTest',
    color: '#6A5AF9',
    fetchUrl: (applicantId, project, positionId) =>
      `/disc-tests/by-position?positionId=${positionId}&projectId=${project}&applicantId=${applicantId}`,
    processUrl: (testId) => `/disc-tests/${testId}/process`,
  },
  reasoning_test: {
    labelKey: 'ats.modules.reasoningTest',
    color: '#0EA5E9',
    fetchUrl: (applicantId, project, positionId) =>
      `/reasoning-tests/by-position?positionId=${positionId}&projectId=${project}&applicantId=${applicantId}`,
    filterExcludeTypes: ['integrityTest', 'emotionalIntelligence'],
    processUrl: (testId, project) => `/reasoning-tests/${testId}/process?projectId=${project}`,
  },
  personal_skills: {
    labelKey: 'ats.modules.personalSkills',
    color: '#EC4899',
    fetchUrl: (applicantId, project, positionId) =>
      `/reasoning-tests/by-position?positionId=${positionId}&projectId=${project}&applicantId=${applicantId}`,
    filterTypes: ['integrityTest', 'emotionalIntelligence'],
    processUrl: (testId, project) => `/reasoning-tests/${testId}/process?projectId=${project}`,
  },
  sales_assessment: {
    labelKey: 'ats.modules.salesAssessment',
    color: '#F59E0B',
    fetchUrl: (applicantId, project, positionId) =>
      `/sales-assessments/by-position?positionId=${positionId}&projectId=${project}&applicantId=${applicantId}`,
    processUrl: (testId) => `/sales-assessments/${testId}/process`,
  },
};

const STATUS_CONFIG = {
  pending: { labelKey: 'ats.testStatus.pending', color: 'default' },
  sent: { labelKey: 'ats.testStatus.sent', color: 'info' },
  started: { labelKey: 'ats.testStatus.started', color: 'info' },
  completed: { labelKey: 'ats.testStatus.completed', color: 'warning' },
  processing: { labelKey: 'ats.testStatus.processing', color: 'warning' },
  evaluated: { labelKey: 'ats.testStatus.evaluated', color: 'success' },
  processing_error: { labelKey: 'ats.testStatus.error', color: 'error' },
  expired: { labelKey: 'ats.testStatus.expired', color: 'error' },
  invalid: { labelKey: 'ats.testStatus.invalid', color: 'error' },
};

// Estados que habilitan un reenvío del test desde el drawer
// (además del caso "sin envío registrado").
const RESEND_STATUSES = ['expired', 'invalid', 'processing_error'];

const INTERVIEW_STATUS_CONFIG = {
  pending: { labelKey: 'ats.interviewStatus.botInMeeting', color: 'info' },
  pending_assignment: { labelKey: 'ats.interviewStatus.assignParticipants', color: 'warning' },
  completed: { labelKey: 'ats.interviewStatus.completed', color: 'success' },
  error: { labelKey: 'ats.interviewStatus.error', color: 'error' },
};

const AtsApplicationDrawer = ({
  open, onClose, application, position, positionId, canEdit = true,
  onDiscard, onRestore, rejectionTemplate = { subject: '', body: '' }, autoSendDefault = false,
  positionName = '', orgName = '',
  canBlock = false, canUnblock = false, onBlockClick, onUnblock,
  cvScore = null,
  onNotesChange,
  onPositionChange,
  // (applicationId, patch) => void: actualiza la postulación en el kanban
  // (la tarjeta) sin recargar; hoy, la revisión de antecedentes recalculada
  onApplicationChange,
  // Cambio de etapa desde la ficha (el kanban abre el mismo diálogo que el
  // arrastre y confirma): versión vigente de la postulación en la lista del
  // kanban, (application, stageId) => void, motivo por el que no se puede
  // mover (null = se puede) y si hay un movimiento guardándose
  liveApplication = null,
  onMoveStage,
  stageLockReason = null,
  movingStage = false,
  // Navegación entre los candidatos de la etapa desde la que se abrió la
  // ficha (null = sin recorrido): { index, total, stageName, onPrev, onNext }.
  // index -1 = el abierto ya salió de la etapa; onPrev / onNext null en los extremos
  navigation = null,
  // Al pasar de un candidato a otro la ficha aparece sin animación
  instantOpen = false,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  // Precio de la verificación de antecedentes (2026-11-01, PLAN_COBRO_ANTECEDENTES)
  const { points: checkPoints } = useCheckPoints();
  const { project, organization, authToken, showAlert } = useStore.getState();
  // Antecedentes (consulta y revisión): solo organizaciones que operan en
  // Chile; fuera de Chile está en espera y no se muestra (PLAN_MASIVO_PORTAL)
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  const backgroundAvailable = !orgCountryLoading && operatesInChile;

  const [discardOpen, setDiscardOpen] = useState(false);
  const [requestDocsOpen, setRequestDocsOpen] = useState(false);
  const [docRequest, setDocRequest] = useState(application?.documentRequest || null);

  useEffect(() => {
    setDocRequest(application?.documentRequest || null);
  }, [application]);

  const [testStatuses, setTestStatuses] = useState({});
  const [loadingTests, setLoadingTests] = useState(false);
  const [processingTest, setProcessingTest] = useState({});
  const [resultTest, setResultTest] = useState(null);
  const [resultModuleId, setResultModuleId] = useState(null);
  const [psicoReport, setPsicoReport] = useState(null);
  const [loadingPsico, setLoadingPsico] = useState(false);

  // Notas del candidato: manuales (application.notes) + edición del texto de
  // las notas de cambio de etapa (stageHistory). Estado local sincronizado
  // desde la application; los cambios se avisan al kanban vía onNotesChange.
  const [manualNotes, setManualNotes] = useState([]);
  const [stageHistoryNotes, setStageHistoryNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // { type: 'manual'|'stage', key, index?, noteId?, text }
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState(null);

  useEffect(() => {
    setManualNotes(application?.notes || []);
    setStageHistoryNotes(application?.stageHistory || []);
    setNewNoteText('');
    setEditingNote(null);
    setConfirmDeleteNoteId(null);
  }, [application]);

  // Versión vigente de la postulación (la ficha recibe la foto del momento en
  // que se abrió): etapa e historial actualizados después de moverla de etapa
  const live = liveApplication
    && (liveApplication.uid || liveApplication.id) === (application?.uid || application?.id)
    ? liveApplication
    : null;
  const liveStageHistory = live?.stageHistory;

  // La nota del movimiento aparece en Notas sin recargar
  useEffect(() => {
    if (liveStageHistory) setStageHistoryNotes(liveStageHistory);
  }, [liveStageHistory]);

  // Conversación del bot de captación (solo vacantes masivas)
  const [botConversation, setBotConversation] = useState(null);
  const [botConversationOpen, setBotConversationOpen] = useState(false);

  useEffect(() => {
    setBotConversation(null);
    setBotConversationOpen(false);
    if (!open || position?.tipo !== 'massive' || !application?.applicantId) return;

    const fetchBotConversation = async () => {
      try {
        const { data } = await api.get(
          `/captation/${positionId}/conversation/by-applicant/${application.applicantId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (data.response?.status_response === 'OK') {
          setBotConversation(data.response.data);
        }
      } catch (err) {
        // 404 = el postulante no llegó por el bot; silencio
        if (err.response?.status !== 404) console.error('Error cargando conversación del bot:', err);
      }
    };
    fetchBotConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, application?.applicantId, position?.tipo]);

  // Antecedentes en el formato común del backend: Chapi, Alto o el legado de
  // Alto (PLAN_MIGRACION_CHAPI). El back refresca si algo quedó en proceso y
  // recalcula la revisión de esta postulación. Se muestran en vacantes con el
  // módulo "Verificación de antecedentes" en alguna etapa (hunting o masivo,
  // PLAN_MASIVO_PORTAL) y en masivas, por los checks que hizo el bot antes.
  const showBackgroundCheck = backgroundAvailable && (position?.tipo === 'massive'
    || hasBackgroundCheckStage(position?.pipeline));
  const [backgroundCheck, setBackgroundCheck] = useState(null);
  const [canRetryCheck, setCanRetryCheck] = useState(false);
  const [retryingCheck, setRetryingCheck] = useState(false);
  // Ya se leyó la consulta guardada (para no mostrar "Sin consultar" mientras carga)
  const [checkLoaded, setCheckLoaded] = useState(false);
  // "Volver a consultar": consulta nueva en Chapi, con su costo (2026-10-13)
  const [confirmRecheck, setConfirmRecheck] = useState(false);
  const [recheckingCheck, setRecheckingCheck] = useState(false);
  // Revisión recalculada que devuelve la consulta (al abrir el drawer o al
  // reintentar): se muestra aquí y pasa a la tarjeta del kanban sin recargar
  // (2026-10-08). No trae la decisión del consultor: se conserva la guardada.
  const [liveReview, setLiveReview] = useState(null);
  // computedAt de la última revisión que puso la propia ficha (ver el efecto
  // de la consulta automática más abajo)
  const ownReviewStamp = useRef(null);
  const applyReview = (review) => {
    if (!review || !application) return;
    const merged = { ...(application.backgroundReview || {}), ...review };
    ownReviewStamp.current = merged.computedAt || null;
    setLiveReview(merged);
    if (onApplicationChange) onApplicationChange(application.uid || application.id, { backgroundReview: merged });
  };

  // Consulta guardada (lectura gratis: el back refresca solo lo que quedó en
  // proceso) y revisión recalculada de esta postulación
  const fetchBackgroundCheck = async () => {
    try {
      const { data } = await api.get(
        `/captation/background-check/${application.applicantId}?applicationId=${application.uid || application.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        setBackgroundCheck(data.response.data);
        setCanRetryCheck(Boolean(data.response.canRetry));
        applyReview(data.response.backgroundReview);
      }
    } catch (err) {
      if (err.response?.status !== 404) console.error('Error cargando antecedentes:', err);
    } finally {
      setCheckLoaded(true);
    }
  };

  useEffect(() => {
    setBackgroundCheck(null);
    setCanRetryCheck(false);
    setLiveReview(null);
    setConfirmRecheck(false);
    setCheckLoaded(false);
    ownReviewStamp.current = application?.backgroundReview?.computedAt || null;
    if (!open || !showBackgroundCheck || !application?.applicantId) return;
    fetchBackgroundCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, application?.applicantId, showBackgroundCheck]);

  // Consulta automática al moverlo con la ficha abierta (etapa con el módulo y
  // "Consultar automáticamente"): el kanban trae una revisión nueva y aquí se
  // vuelve a leer la consulta para mostrarla (lectura gratis). Las revisiones
  // que puso la propia ficha se ignoran (evita leer en ciclo).
  const liveReviewStamp = live?.backgroundReview?.computedAt || null;
  useEffect(() => {
    if (!open || !showBackgroundCheck || !application?.applicantId) return;
    if (!liveReviewStamp || liveReviewStamp === ownReviewStamp.current) return;
    ownReviewStamp.current = liveReviewStamp;
    fetchBackgroundCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveReviewStamp]);

  // Reintento manual (fase 3): el botón solo aparece si la consulta terminó en
  // error y el postulante tiene RUT chileno válido (canRetry lo decide el back)
  const handleRetryBackgroundCheck = async () => {
    setRetryingCheck(true);
    try {
      const { data } = await api.post(
        `/captation/background-check/${application.applicantId}`,
        { applicationId: application.uid || application.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        const failedAgain = data.response.data?.status === 'error';
        setBackgroundCheck(data.response.data);
        setCanRetryCheck(Boolean(data.response.canRetry));
        applyReview(data.response.backgroundReview);
        showAlert(
          t(failedAgain ? 'ats.backgroundCheck.retryFailedAgain' : 'ats.backgroundCheck.retryOk'),
          failedAgain ? 'warning' : 'success'
        );
      }
    } catch (err) {
      console.error('Error reintentando antecedentes:', err);
      // Sin puntos o sin el servicio (2026-11-01): no se consultó
      const block = pointsBlockFromError(err);
      showAlert(block ? pointsBlockMessage(t, block) : t('ats.backgroundCheck.retryError'), block ? 'warning' : 'error');
    } finally {
      setRetryingCheck(false);
    }
  };

  // "Volver a consultar" (2026-10-13): consulta nueva en Chapi aunque haya
  // una reciente, con el mismo endpoint del modal de la etapa (force). Quien
  // puede editar la vacante; se confirma antes por el costo.
  const handleRecheckBackground = async () => {
    if (!application) return;
    setRecheckingCheck(true);
    try {
      const { data } = await api.post(
        `/captation/background-checks/application/${application.uid || application.id}`,
        { force: true },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const response = data.response || {};
      if (response.status_response === 'OK') {
        const failed = response.check?.status === 'error';
        if (response.check) {
          setBackgroundCheck(response.check);
          setCanRetryCheck(failed);
        }
        applyReview(response.backgroundReview);
        setConfirmRecheck(false);
        showAlert(
          t(failed ? 'ats.backgroundCheck.retryFailedAgain' : 'ats.backgroundCheckStage.forceRecheckDone'),
          failed ? 'warning' : 'success'
        );
      }
    } catch (err) {
      console.error('Error volviendo a consultar antecedentes:', err);
      const block = pointsBlockFromError(err);
      const reason = err.response?.data?.response?.reason;
      if (block) showAlert(pointsBlockMessage(t, block), 'warning');
      else showAlert(reason ? t(`ats.backgroundCheckStage.reason.${reason}`) : t('ats.backgroundCheckStage.verifyError'), 'error');
    } finally {
      setRecheckingCheck(false);
    }
  };

  // Revisión de antecedentes (F3): decisión del consultor sobre la bandeja
  const [reviewDecision, setReviewDecision] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [confirmReview, setConfirmReview] = useState(null); // 'approved' | 'rejected' | null

  useEffect(() => {
    setReviewDecision(application?.backgroundReview?.decision || null);
    setReviewNote('');
    setConfirmReview(null);
  }, [application]);

  const handleReviewDecision = async (decision) => {
    setSavingReview(true);
    try {
      const { data } = await api.put(
        `/captation/background-review/${application.uid || application.id}`,
        { decision, note: reviewNote || null },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        setReviewDecision(data.response.data?.backgroundReview?.decision || { status: decision });
        setConfirmReview(null);
        showAlert(t('ats.backgroundReview.decisionSaved'), 'success');
      }
    } catch (err) {
      console.error('Error guardando decisión de revisión:', err);
      showAlert(t('ats.backgroundReview.decisionError'), 'error');
    } finally {
      setSavingReview(false);
    }
  };

  // Resumen CV con IA (se guarda a nivel de applicant)
  const [applicantData, setApplicantData] = useState(null);
  const [loadingApplicant, setLoadingApplicant] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  // Envío de tests/entrevista/psicolaboral desde el drawer:
  // solo módulos de la etapa ACTUAL del candidato (coherencia etapa↔test).
  const [sendModuleId, setSendModuleId] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [psychoDialogOpen, setPsychoDialogOpen] = useState(false);

  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [assigningInterview, setAssigningInterview] = useState(null);
  const [selectedIntervieweeId, setSelectedIntervieweeId] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);

  const getAllStages = () => {
    const stages = [];
    (position?.pipeline?.phases || []).forEach((phase) => {
      (phase.stages || []).forEach((stage) => {
        stages.push({ ...stage, phaseId: phase.id, phaseName: phase.name });
      });
    });
    return stages;
  };

  // Etapa vigente: la de la lista del kanban si ya se movió con la ficha abierta
  const currentStageId = live?.currentStageId ?? application?.currentStageId;
  const currentStage = getAllStages().find((s) => s.id === currentStageId);

  // Selector de etapas de la ficha: una fila por fase, en el orden del kanban
  const pipelinePhases = position?.pipeline?.phases || [];
  const canMoveStage = !!onMoveStage && !stageLockReason && !movingStage;
  const stageLockText = stageLockReason ? t(`ats.stageSelector.lock.${stageLockReason}`) : '';

  const allModules = getAllStages()
    .flatMap((s) => s.modules || [])
    .filter((m) => TEST_CONFIG[m]);
  const stageModules = [...new Set(allModules)];

  const hasInterview = getAllStages().some((s) => s.modules?.includes('interview'));
  const hasPsicolaboral = getAllStages().some((s) => s.modules?.includes('psicolaboral'));

  // Módulos de la etapa actual del candidato: los únicos con acción de envío.
  const currentStageModules = currentStage?.modules || [];
  // "Por validar" tampoco envía nada: la ficha puede no tener correo todavía
  const canSendActions =
    canEdit &&
    !!application?.applicantId &&
    application?.status !== 'discarded' &&
    application?.status !== 'blocked' &&
    application?.status !== 'pending_validation';

  // 'send' si el módulo es de la etapa actual y no hay envío; 'resend' si algún
  // envío quedó expirado, inválido o con error; null en cualquier otro caso.
  const getSendAction = (moduleId, tests) => {
    if (!canSendActions || !currentStageModules.includes(moduleId)) return null;
    if (!tests || tests.length === 0) return 'send';
    if (tests.some((tst) => RESEND_STATUSES.includes(tst?.status))) return 'resend';
    return null;
  };

  useEffect(() => {
    if (!open || !application?.applicantId) {
      setTestStatuses({});
      setInterviews([]);
      setAssigningInterview(null);
      setPsicoReport(null);
      setApplicantData(null);
      setConfirmRegenerate(false);
      return;
    }
    if (stageModules.length > 0) loadTestStatuses();
    if (hasInterview) fetchInterviews();
    if (hasPsicolaboral) fetchPsicoReport();
    fetchApplicantData();
  }, [open, application]);

  // Carga el applicant para la sección de resumen CV (cvUrl + cvSummary)
  const fetchApplicantData = async () => {
    setLoadingApplicant(true);
    try {
      const { data } = await api.get(
        `/applicants/${application.applicantId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        setApplicantData(data.response.data);
      }
    } catch (err) {
      console.error('Error cargando applicant:', err);
    } finally {
      setLoadingApplicant(false);
    }
  };

  const handleGenerateSummary = async (force = false) => {
    setGeneratingSummary(true);
    try {
      const { data } = await api.post(
        `/applicants/${application.applicantId}/cv-summary`,
        { organizationId: organization, language: lang, force },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        setApplicantData((prev) => ({ ...prev, cvSummary: data.response.data?.cvSummary }));
        showAlert(t('ats.cvSummary.generated'), 'success');
      } else {
        showAlert(data.response?.message || t('ats.cvSummary.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      const status = err.response?.status;
      if (status === 403) showAlert(t('ats.cvSummary.limitReached'), 'error');
      else if (status === 422) showAlert(t('ats.cvSummary.unreadable'), 'error');
      else showAlert(err.response?.data?.response?.message || t('ats.cvSummary.error'), 'error');
    } finally {
      setGeneratingSummary(false);
      setConfirmRegenerate(false);
    }
  };

  const loadTestStatuses = async () => {
    if (!application?.applicantId) return;
    setLoadingTests(true);
    const statuses = {};

    await Promise.all(
      stageModules.map(async (moduleId) => {
        const config = TEST_CONFIG[moduleId];
        try {
          const url = config.fetchUrl(application.applicantId, project, positionId);
          const response = await api.get(url, { headers: { Authorization: `Bearer ${authToken}` } });
          let data = response.data.response?.data || [];

          if (config.filterTypes) {
            data = data.filter((t) => config.filterTypes.includes(t.testType));
          } else if (config.filterExcludeTypes) {
            data = data.filter((t) => !config.filterExcludeTypes.includes(t.testType));
          }

          // Guardar TODOS los tests del módulo (puede haber varios subtipos
          // evaluados, p. ej. capacidades: numérico, lógico, etc.)
          statuses[moduleId] = Array.isArray(data) ? data : [];
        } catch (err) {
          console.error(`Error fetching ${moduleId}:`, err);
          statuses[moduleId] = [];
        }
      })
    );

    setTestStatuses(statuses);
    setLoadingTests(false);
  };

  // Estado del psicolaboral: se resuelve por el informe vinculado a la aplicación
  // (application.psycholaboralReportId), no por el patrón by-position de los tests.
  const fetchPsicoReport = async () => {
    const reportId = application?.psycholaboralReportId;
    if (!reportId) {
      setPsicoReport(null);
      return;
    }
    setLoadingPsico(true);
    try {
      const { data } = await api.get(
        `/reports/${reportId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const rep = data.response?.data ?? data.data ?? null;
      setPsicoReport(Array.isArray(rep) ? (rep[0] || null) : rep);
    } catch (err) {
      console.error('Error fetching psicolaboral report:', err);
      setPsicoReport(null);
    } finally {
      setLoadingPsico(false);
    }
  };

  // Actualiza UN test dentro del array del módulo (identificado por testId)
  const updateModuleTest = (moduleId, testId, changes) => {
    setTestStatuses((prev) => ({
      ...prev,
      [moduleId]: (prev[moduleId] || []).map((tst) =>
        (tst.id || tst.uid) === testId ? { ...tst, ...changes } : tst
      ),
    }));
  };

  const handleProcessTest = async (moduleId, testId) => {
    const config = TEST_CONFIG[moduleId];
    setProcessingTest((prev) => ({ ...prev, [testId]: true }));
    try {
      const url = config.processUrl(testId, project);
      await api.post(url, {}, { headers: { Authorization: `Bearer ${authToken}` } });
      showAlert(t('ats.drawer.testProcessing'), 'success');

      updateModuleTest(moduleId, testId, { status: 'processing' });

      const collectionName = COLLECTION_MAP[moduleId];
      const testRef = doc(db, collectionName, testId);
      const unsubscribe = onSnapshot(testRef, (docSnap) => {
        if (!docSnap.exists()) return;
        const data = docSnap.data();
        if (data.status !== 'processing') {
          unsubscribe();
          updateModuleTest(moduleId, testId, { ...data, id: testId });
          setProcessingTest((prev) => ({ ...prev, [testId]: false }));
        }
      });
    } catch (err) {
      console.error(err);
      showAlert(t('ats.drawer.testProcessError'), 'error');
      setProcessingTest((prev) => ({ ...prev, [testId]: false }));
    }
  };

  // Envío del test desde el drawer. Reutiliza MODULE_CONFIG (endpoint + body)
  // de AtsStageColumn; misma firma que el handler del kanban porque el
  // AtsTestSendDialog lo invoca igual (aquí siempre con un solo candidato).
  const handleSendBulk = async (selectedCandidateIds, selectedTypes = null, discOptions = null) => {
    if (!sendModuleId || !application) return;
    setSendingTest(true);

    const config = MODULE_CONFIG[sendModuleId];
    const selected = [application].filter((a) => selectedCandidateIds.includes(a.uid || a.id));

    let sent = 0;
    let failed = 0;

    for (const app of selected) {
      if (!app.applicantId) { failed++; continue; }
      try {
        const body = config.buildBody(
          app,
          organization,
          project,
          positionId,
          position?.name,
          position?.descripcion || '',
          lang,
          selectedTypes,
          discOptions
        );
        await api.post(
          `/${config.endpoint}`,
          body,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        sent++;
      } catch (err) {
        console.error(err);
        failed++;
      }
    }

    setSendingTest(false);
    setSendResults({ sent, failed });
    if (sent > 0) loadTestStatuses();
  };

  const fetchInterviews = async () => {
    const applicationId = application?.uid || application?.id;
    if (!positionId || !applicationId) {
      setInterviews([]);
      return;
    }
    setLoadingInterviews(true);
    try {
      // Solo las entrevistas de esta vacante, filtradas por la postulación
      // (las creadas fuera del pipeline no tienen positionId/applicationId y no aparecen).
      const res = await api.get(
        `/interviews/by-position`,
        {
          params: { positionId },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.errorCode === 0) {
        const data = res.data.response?.data;
        const list = Array.isArray(data) ? data : [];
        setInterviews(list.filter((i) => i.applicationId === applicationId));
      } else {
        setInterviews([]);
      }
    } catch (err) {
      console.error(err);
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  const handleAssignParticipants = async (interview) => {
    if (!selectedIntervieweeId) return;
    const interviewee = interview.participants.find((p) => p.id === Number(selectedIntervieweeId));
    setLoadingAssign(true);
    try {
      const res = await api.put(
        `/applicants/${application.applicantId}/interviews/${interview.uid}/assign-participants`,
        {
          intervieweeId: interviewee.id,
          intervieweeName: interviewee.name,
          applicantName: application.candidateName || application.candidateEmail,
          meetingDate: interview.createdAt?._seconds
            ? new Date(interview.createdAt._seconds * 1000).toISOString()
            : null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.errorCode === 0) {
        showAlert(t('interviews.assignedSuccess'), 'success');
        setAssigningInterview(null);
        setSelectedIntervieweeId('');
        await fetchInterviews();
      } else {
        showAlert(t('interviews.assignError'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.assignError'), 'error');
    } finally {
      setLoadingAssign(false);
    }
  };

  // ── Notas: handlers ───────────────────────────────────────────────────────

  const notifyNotesChange = (patch) => {
    if (onNotesChange && application) {
      onNotesChange(application.uid || application.id, patch);
    }
  };

  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(
        `/applications/${application.uid || application.id}/notes`,
        { text },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        const updated = [...manualNotes, data.response.data];
        setManualNotes(updated);
        setNewNoteText('');
        notifyNotesChange({ notes: updated });
      } else {
        showAlert(data.response?.message || t('ats.notes.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.response?.message || t('ats.notes.error'), 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveNoteEdit = async () => {
    if (!editingNote) return;
    const text = editingNote.text.trim();
    if (editingNote.type === 'manual' && !text) return;
    setSavingNote(true);
    try {
      if (editingNote.type === 'manual') {
        const { data } = await api.put(
          `/applications/${application.uid || application.id}/notes/${editingNote.noteId}`,
          { text },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (data.response?.status_response === 'OK') {
          const updated = manualNotes.map((n) => (n.id === editingNote.noteId ? data.response.data : n));
          setManualNotes(updated);
          setEditingNote(null);
          notifyNotesChange({ notes: updated });
        } else {
          showAlert(data.response?.message || t('ats.notes.error'), 'error');
        }
      } else {
        // Nota de cambio de etapa: se edita solo el TEXTO; texto vacío la elimina
        // de la lista, pero el registro del movimiento se conserva.
        const { data } = await api.patch(
          `/applications/${application.uid || application.id}/stage-history/${editingNote.index}/note`,
          { text },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (data.response?.status_response === 'OK') {
          const updated = stageHistoryNotes.map((h, i) => (i === editingNote.index ? data.response.data : h));
          setStageHistoryNotes(updated);
          setEditingNote(null);
          notifyNotesChange({ stageHistory: updated });
        } else {
          showAlert(data.response?.message || t('ats.notes.error'), 'error');
        }
      }
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.response?.message || t('ats.notes.error'), 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteManualNote = async (noteId) => {
    setSavingNote(true);
    try {
      const { data } = await api.delete(
        `/applications/${application.uid || application.id}/notes/${noteId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        const updated = manualNotes.filter((n) => n.id !== noteId);
        setManualNotes(updated);
        notifyNotesChange({ notes: updated });
      } else {
        showAlert(data.response?.message || t('ats.notes.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.response?.message || t('ats.notes.error'), 'error');
    } finally {
      setSavingNote(false);
      setConfirmDeleteNoteId(null);
    }
  };

  if (!application || !position) return null;

  const candidateName = application.candidateName || application.candidateEmail || '—';
  const candidateEmail = application.candidateEmail || '';
  // Teléfono y documento de la ficha (PLAN_IMPORTACION_COMPUTRABAJO, fase 1):
  // el RUT con puntos y guion solo en organizaciones de Chile (el documento se
  // muestra cuando ya se sabe el país)
  const contactLine = [
    applicantData?.phone ? formatValidationPhone(applicantData.phone) : null,
    applicantData?.rut && !orgCountryLoading
      ? (operatesInChile
        ? t('ats.import.contactRut', { rut: formatRut(applicantData.rut) })
        : t('ats.import.contactDocument', { document: applicantData.rut }))
      : null,
  ].filter(Boolean).join(' · ');
  // Origen: importada desde un archivo → el portal elegido al importar
  const sourceLabel = application.source === 'import'
    ? t('ats.import.sourceLabel', { origin: application.importOrigin || '—' })
    : (application.source || 'portal');

  const formatTs = (ts) => {
    if (!ts) return '—';
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('es-CL');
    return '—';
  };

  const translateAnswer = (val) => {
    if (val === 'yes') return t('yes');
    if (val === 'no') return t('no');
    return val;
  };

  // Timeline unificado de notas: manuales + de cambio de etapa (solo con texto),
  // ordenadas de la más reciente a la más antigua. Los datos viven separados
  // (notes / stageHistory); la mezcla es solo presentación.
  const tsMillis = (ts) => {
    if (!ts) return 0;
    if (ts._seconds) return ts._seconds * 1000;
    if (ts.toDate) return ts.toDate().getTime();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const timelineNotes = [
    ...manualNotes.map((n) => ({
      type: 'manual', key: `manual-${n.id}`, note: n, at: tsMillis(n.createdAt),
    })),
    ...stageHistoryNotes
      .map((entry, index) => ({
        type: 'stage', key: `stage-${index}`, index, entry, at: tsMillis(entry.changedAt),
      }))
      .filter((item) => item.entry.note),
  ].sort((a, b) => b.at - a.at);

  // Atajos ← / → del recorrido: solo con el foco dentro de la ficha (no en un
  // menú ni en un modal abierto encima), sin modificadores (Alt+← es "atrás"
  // del navegador), sin repetir al mantener la tecla y nunca mientras se
  // escribe en un campo o se usa un selector
  const handleNavKeyDown = (event) => {
    if (!navigation || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.repeat) return;
    const { target } = event;
    if (!(target instanceof Element) || !event.currentTarget.contains(target)) return;
    if (target.isContentEditable
      || target.closest('input, textarea, select, [role="combobox"], [role="listbox"], [role="menu"], [role="slider"], [role="radiogroup"], [role="tablist"]')) return;
    const go = event.key === 'ArrowLeft' ? navigation.onPrev : navigation.onNext;
    if (!go) return;
    event.preventDefault();
    go();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        // Al pasar de un candidato a otro del recorrido aparece sin animación
        transitionDuration={instantOpen ? 0 : undefined}
        // Hasta la mitad de la pantalla, nunca más angosta que antes (420 px)
        // salvo en pantallas más chicas que eso
        PaperProps={{
          sx: { width: '50vw', minWidth: 'min(420px, 100vw)', mt: '64px', height: 'calc(100% - 64px)' },
          onKeyDown: handleNavKeyDown,
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              {/* minWidth 0: un nombre o correo largo deja lugar a la navegación y a la X */}
              <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700}>{candidateName}</Typography>
                  {typeof cvScore === 'number' && (
                    <Tooltip title={t('ats.pipeline.cvScoreTooltip')}>
                      <Chip
                        label={`${cvScore}%`}
                        size="small"
                        sx={{
                          fontWeight: 700, flexShrink: 0,
                          backgroundColor: cvScore >= 70 ? '#DCFCE7' : cvScore >= 50 ? '#FEF9C3' : '#FEE2E2',
                          color: cvScore >= 70 ? '#16A34A' : cvScore >= 50 ? '#CA8A04' : '#DC2626',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {candidateEmail && (
                  <Typography variant="body2" color="text.secondary">{candidateEmail}</Typography>
                )}
                {contactLine && (
                  <Typography variant="body2" color="text.secondary">{contactLine}</Typography>
                )}
                {/* Origen del contacto (2026-09-07): WhatsApp libre / link de cargo / web */}
                {applicantData?.contactSource && (
                  <Chip
                    label={t(`ats.contactSource.${applicantData.contactSource}`, { defaultValue: applicantData.contactSource })}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {/* Recorrido: anterior / posición / siguiente dentro de la etapa
                    desde la que se abrió la ficha (no da la vuelta) */}
                {navigation && (
                  <>
                    <Tooltip title={`${t('ats.drawer.prevCandidate')} (←)`}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('ats.drawer.prevCandidate')}
                          disabled={!navigation.onPrev}
                          onClick={() => navigation.onPrev && navigation.onPrev()}
                        >
                          <PrevIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      describeChild
                      title={navigation.index >= 0
                        ? t('ats.drawer.navStageHint', { stage: navigation.stageName })
                        : t('ats.drawer.navOutOfStageHint', { stage: navigation.stageName })}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="drawer-nav-position"
                        sx={{ whiteSpace: 'nowrap', userSelect: 'none' }}
                      >
                        {navigation.index >= 0
                          ? t('ats.drawer.navPosition', { index: navigation.index + 1, total: navigation.total })
                          : t('ats.drawer.navOutOfStage', { total: navigation.total })}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={`${t('ats.drawer.nextCandidate')} (→)`}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('ats.drawer.nextCandidate')}
                          disabled={!navigation.onNext}
                          onClick={() => navigation.onNext && navigation.onNext()}
                        >
                          <NextIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {canEdit && application?.status !== 'discarded' && application?.status !== 'blocked' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DiscardIcon fontSize="small" />}
                  onClick={() => setDiscardOpen(true)}
                >
                  {t('ats.detail.discard')}
                </Button>
              )}

              {/* Restaurar un candidato descartado: vuelve a la etapa donde estaba.
                  La llamada al backend vive en el padre (AtsKanbanPipeline), igual
                  que onDiscard/onUnblock, para sincronizar el estado del kanban. */}
              {canEdit && application?.status === 'discarded' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<RestoreIcon fontSize="small" />}
                  onClick={() => onRestore && onRestore(application)}
                >
                  {t('ats.detail.restore')}
                </Button>
              )}

              {/* La lista negra es por correo: sin correo no hay qué bloquear */}
              {canBlock && application?.status !== 'blocked' && !!application?.candidateEmail && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BlockIcon fontSize="small" />}
                  onClick={() => onBlockClick && onBlockClick(application)}
                  sx={{ borderColor: '#374151 !important', color: '#374151 !important' }}
                >
                  {t('ats.detail.block')}
                </Button>
              )}

              {canUnblock && application?.status === 'blocked' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<LockOpenIcon fontSize="small" />}
                  onClick={() => onUnblock && onUnblock(application)}
                >
                  {t('ats.detail.unblock')}
                </Button>
              )}
            </Box>

            {application?.status === 'blocked' && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t('ats.detail.blockedChip')}
                  size="small"
                  sx={{ backgroundColor: '#374151', color: '#fff', fontWeight: 600 }}
                />
                {application?.blockReason && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t('ats.detail.blockReason')}: {application.blockReason}
                  </Typography>
                )}
              </Box>
            )}

            {application?.status === 'discarded' && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t('ats.detail.discardedChip')}
                  size="small"
                  sx={{ backgroundColor: '#DC2626', color: '#fff', fontWeight: 600 }}
                />
              </Box>
            )}

            {/* "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): motivo y teléfono */}
            {application?.status === 'pending_validation' && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t('ats.validation.chip')}
                  size="small"
                  sx={{ backgroundColor: '#D97706', color: '#fff', fontWeight: 600 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {validationReasonText(t, application.validation)}
                </Typography>
                {application.validation?.contactPhone && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('ats.validation.phone', { phone: formatValidationPhone(application.validation.contactPhone) })}
                  </Typography>
                )}
              </Box>
            )}

            <Divider />
          </Box>

          {/* Contenido scrollable */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>

            {/* Etapa actual: todas las etapas de la vacante, una fila por fase,
                con la actual resaltada. Clic en otra etapa → el kanban abre el
                mismo diálogo del arrastre (nota opcional) y confirma; clic en la
                actual no hace nada. Sin poder mover: solo lectura con el motivo. */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('ats.modules.currentStage')}
                </Typography>
                {movingStage && <CircularProgress size={12} />}
              </Box>
              {pipelinePhases.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>—</Typography>
              ) : (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {pipelinePhases.map((phase) => {
                    const isCurrentPhase = phase.id === currentStage?.phaseId;
                    return (
                      <Box key={phase.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            width: 130, flexShrink: 0, pt: '4px', lineHeight: 1.3,
                            color: isCurrentPhase ? '#6A5AF9' : 'text.secondary',
                            fontWeight: isCurrentPhase ? 700 : 500,
                          }}
                        >
                          {phase.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                          {(phase.stages || []).map((stage) => {
                            const isCurrent = stage.id === currentStageId;
                            const clickable = !isCurrent && canMoveStage;
                            let tooltip = '';
                            if (isCurrent) tooltip = t('ats.modules.currentStage');
                            else if (stageLockReason) tooltip = stageLockText;
                            else if (onMoveStage) tooltip = t('ats.stageSelector.moveTo', { stage: stage.name });
                            return (
                              <Tooltip key={stage.id} title={tooltip} arrow describeChild>
                                <Chip
                                  label={stage.name}
                                  size="small"
                                  variant={isCurrent ? 'filled' : 'outlined'}
                                  onClick={clickable ? () => onMoveStage(application, stage.id) : undefined}
                                  sx={isCurrent
                                    ? { backgroundColor: '#6A5AF9', color: '#fff', fontWeight: 600 }
                                    : {
                                      borderColor: '#D1D5DB',
                                      color: clickable ? 'text.primary' : 'text.secondary',
                                      '&.MuiChip-clickable:hover': {
                                        backgroundColor: '#F5F3FF',
                                        borderColor: '#6A5AF9',
                                        color: '#6A5AF9',
                                      },
                                    }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {stageLockReason && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: 14, color: 'text.secondary', mt: '2px' }} />
                  <Typography variant="caption" color="text.secondary">
                    {stageLockText}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Resumen CV (IA) — a demanda, guardado a nivel de applicant */}
            {application?.applicantId && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('ats.cvSummary.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {applicantData?.cvUrl && (
                        <Tooltip title={t('ats.cvSummary.viewCv')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(applicantData.cvUrl, '_blank', 'noopener,noreferrer')}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {applicantData?.cvSummary && !confirmRegenerate && (
                        <Button
                          size="small"
                          startIcon={<AiIcon fontSize="small" />}
                          onClick={() => setConfirmRegenerate(true)}
                          disabled={generatingSummary}
                          sx={{ fontSize: '0.7rem', color: '#7C3AED' }}
                        >
                          {t('ats.cvSummary.regenerate')}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {loadingApplicant ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : !applicantData?.cvUrl ? (
                    <Typography variant="caption" color="text.disabled">
                      {t('ats.cvSummary.noCv')}
                    </Typography>
                  ) : (
                    <>
                      {/* Confirmación de regeneración (cobra 0.1 pts) */}
                      {confirmRegenerate && (
                        <Paper elevation={0} sx={{ p: 1.5, mb: 1, backgroundColor: '#FEF9C3', border: '1px solid #FDE047', borderRadius: 1.5 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                            {t('ats.cvSummary.regenerateConfirm')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" color="inherit" onClick={() => setConfirmRegenerate(false)} disabled={generatingSummary} sx={{ fontSize: '0.7rem' }}>
                              {t('cancel')}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleGenerateSummary(true)}
                              disabled={generatingSummary}
                              startIcon={generatingSummary ? <CircularProgress size={12} color="inherit" /> : null}
                              sx={{ fontSize: '0.7rem', backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                            >
                              {t('ats.cvSummary.regenerateAction')}
                            </Button>
                          </Box>
                        </Paper>
                      )}

                      {applicantData.cvSummary ? (
                        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #E5E7EB', borderRadius: 1.5 }}>
                          {applicantData.cvSummary.cvUrl && applicantData.cvSummary.cvUrl !== applicantData.cvUrl && (
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#CA8A04', fontWeight: 600 }}>
                              {t('ats.cvSummary.staleWarning')}
                            </Typography>
                          )}

                          {applicantData.cvSummary.data?.perfil && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {applicantData.cvSummary.data.perfil}
                            </Typography>
                          )}

                          {applicantData.cvSummary.data?.aniosExperiencia != null && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>{t('ats.cvSummary.yearsExperience')}:</strong> {applicantData.cvSummary.data.aniosExperiencia}
                            </Typography>
                          )}

                          {applicantData.cvSummary.data?.experiencia?.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                {t('ats.cvSummary.experience')}
                              </Typography>
                              {applicantData.cvSummary.data.experiencia.map((item, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  • {item}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {applicantData.cvSummary.data?.educacion?.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                {t('ats.cvSummary.education')}
                              </Typography>
                              {applicantData.cvSummary.data.educacion.map((item, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  • {item}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {applicantData.cvSummary.data?.skills?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                              {applicantData.cvSummary.data.skills.map((skill, i) => (
                                <Chip key={i} label={skill} size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#EDE9FE', color: '#6A5AF9' }} />
                              ))}
                            </Box>
                          )}

                          {applicantData.cvSummary.data?.idiomas?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                              {applicantData.cvSummary.data.idiomas.map((idioma, i) => (
                                <Chip key={i} label={idioma} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                              ))}
                            </Box>
                          )}

                          {applicantData.cvSummary.data?.alertas?.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', color: '#CA8A04' }}>
                                {t('ats.cvSummary.flags')}
                              </Typography>
                              {applicantData.cvSummary.data.alertas.map((item, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block', color: '#CA8A04' }}>
                                  • {item}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {applicantData.cvSummary.data?.otros && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                              {applicantData.cvSummary.data.otros}
                            </Typography>
                          )}

                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                            {t('ats.cvSummary.generatedOn')}: {formatTs(applicantData.cvSummary.generatedAt)}
                          </Typography>
                        </Paper>
                      ) : (
                        canEdit && (
                          <Button
                            size="small"
                            variant="outlined"
                            fullWidth
                            onClick={() => handleGenerateSummary(false)}
                            disabled={generatingSummary}
                            startIcon={generatingSummary ? <CircularProgress size={14} /> : <AiIcon fontSize="small" />}
                            sx={{
                              fontSize: '0.75rem',
                              borderColor: '#7C3AED', color: '#7C3AED',
                              '&:hover': { borderColor: '#6D28D9', backgroundColor: '#F5F3FF' },
                            }}
                          >
                            {generatingSummary ? t('ats.cvSummary.generating') : t('ats.cvSummary.generate')}
                          </Button>
                        )
                      )}
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Tests */}
            {(stageModules.length > 0 || hasPsicolaboral) && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                    {t('ats.drawer.testsTitle')}
                  </Typography>

                  {loadingTests ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {stageModules.map((moduleId) => {
                        const config = TEST_CONFIG[moduleId];
                        const tests = testStatuses[moduleId] || [];

                        // Etiqueta del subtipo (capacidades / habilidades personales)
                        const getTypeLabel = (testType) => {
                          if (!testType) return null;
                          const kr = `reasoningTests.${testType}`;
                          if (t(kr) !== kr) return t(kr);
                          const kp = `personalHabilitiesTests.${testType}`;
                          if (t(kp) !== kp) return t(kp);
                          return testType;
                        };

                        // Acción de envío solo para módulos de la etapa actual
                        const sendAction = getSendAction(moduleId, tests);

                        return (
                          <Paper key={moduleId} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ color: config.color }}>
                                {t(config.labelKey)}
                              </Typography>
                              {sendAction && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => { setSendResults(null); setSendModuleId(moduleId); }}
                                  sx={{
                                    fontSize: '0.7rem', flexShrink: 0,
                                    borderColor: config.color, color: config.color,
                                    '&:hover': { borderColor: config.color, backgroundColor: `${config.color}11` },
                                  }}
                                >
                                  {sendAction === 'send' ? t('ats.drawer.sendTest') : t('ats.drawer.resendTest')}
                                </Button>
                              )}
                            </Box>
                            {tests.length === 0 ? (
                              <Typography variant="caption" color="text.disabled">
                                {t('ats.drawer.testNotSent')}
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
                                {tests.map((testData) => {
                                  const status = testData?.status;
                                  const statusConf = status ? (STATUS_CONFIG[status] || null) : null;
                                  const testId = testData?.id || testData?.uid;
                                  const isProcessing = processingTest[testId];
                                  const typeLabel = getTypeLabel(testData?.testType);

                                  return (
                                    <Box key={testId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        {typeLabel && (
                                          <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {typeLabel}
                                          </Typography>
                                        )}
                                        {statusConf && (
                                          <Chip label={t(statusConf.labelKey)} color={statusConf.color} size="small"
                                            sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }} />
                                        )}
                                      </Box>

                                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        {canEdit && (status === 'completed' || status === 'processing_error') && (
                                          <Tooltip title={t('ats.drawer.processTest')}>
                                            <span>
                                              <IconButton size="small" color="primary"
                                                onClick={() => handleProcessTest(moduleId, testId)}
                                                disabled={isProcessing}>
                                                {isProcessing ? <CircularProgress size={16} /> : <ProcessIcon fontSize="small" />}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        )}
                                        {status === 'processing' && (
                                          <CircularProgress size={20} sx={{ color: config.color }} />
                                        )}
                                        {status === 'evaluated' && (
                                          <Tooltip title={t('ats.drawer.viewResult')}>
                                            <IconButton size="small" color="success"
                                              onClick={() => { setResultTest(testData); setResultModuleId(moduleId); }}>
                                              <ViewIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                      {hasPsicolaboral && (
                        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#EC4899' }}>
                                {t('ats.modules.psicolaboral')}
                              </Typography>
                              {!application?.psycholaboralReportId ? (
                                <Typography variant="caption" color="text.disabled">
                                  {t('ats.drawer.testNotSent')}
                                </Typography>
                              ) : (
                                <Chip
                                  label={psicoReport?.report_url ? t('ats.testStatus.evaluated') : t('ats.testStatus.processing')}
                                  color={psicoReport?.report_url ? 'success' : 'warning'}
                                  size="small"
                                  sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              {canSendActions &&
                                currentStageModules.includes('psicolaboral') &&
                                !application?.psycholaboralReportId && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => setPsychoDialogOpen(true)}
                                  sx={{
                                    fontSize: '0.7rem', flexShrink: 0,
                                    borderColor: '#EC4899', color: '#EC4899',
                                    '&:hover': { borderColor: '#DB2777', backgroundColor: '#FDF2F8' },
                                  }}
                                >
                                  {t('ats.drawer.generatePsico')}
                                </Button>
                              )}
                              {loadingPsico && <CircularProgress size={18} sx={{ color: '#EC4899' }} />}
                              {psicoReport?.report_url && (
                                <Tooltip title={t('ats.psycholaboral.viewReport')}>
                                  <IconButton size="small" color="success"
                                    onClick={() => window.open(psicoReport.report_url, '_blank')}>
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {psicoReport?.resumen_ejecutivo_pdf_url && (
                                <Tooltip title={t('ats.psycholaboral.downloadSummary')}>
                                  <IconButton size="small" sx={{ color: '#EC4899' }}
                                    onClick={() => window.open(psicoReport.resumen_ejecutivo_pdf_url, '_blank')}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Entrevistas */}
            {hasInterview && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('interviews.title')}
                    </Typography>
                    <IconButton size="small" onClick={fetchInterviews}>
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {loadingInterviews ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : interviews.length === 0 ? (
                    <Box>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        {t('interviews.noInterviews')}
                      </Typography>
                      {canSendActions && currentStageModules.includes('interview') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setInterviewDialogOpen(true)}
                          sx={{
                            mt: 1, fontSize: '0.7rem',
                            borderColor: '#8B5CF6', color: '#8B5CF6',
                            '&:hover': { borderColor: '#7C3AED', backgroundColor: '#F5F3FF' },
                          }}
                        >
                          {t('ats.drawer.registerInterview')}
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {interviews.map((interview) => {
                        const intStatusConf = INTERVIEW_STATUS_CONFIG[interview.status] || INTERVIEW_STATUS_CONFIG.error;
                        return (
                          <Paper key={interview.uid} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatTs(interview.createdAt)}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip label={t(intStatusConf.labelKey)} color={intStatusConf.color} size="small"
                                    sx={{ height: 18, fontSize: '0.65rem' }} />
                                </Box>
                              </Box>
                              {interview.status === 'completed' && interview.transcriptTxtUrl && (
                                <Tooltip title={t('interviews.viewTranscript')}>
                                  <IconButton size="small" color="primary"
                                    onClick={() => window.open(interview.transcriptTxtUrl, '_blank')}>
                                    <TranscriptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            {canEdit && interview.status === 'pending_assignment' && interview.participants?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {assigningInterview?.uid === interview.uid ? (
                                  <>
                                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                                      <InputLabel>{t('interviews.whoIsInterviewee')}</InputLabel>
                                      <Select
                                        value={selectedIntervieweeId}
                                        label={t('interviews.whoIsInterviewee')}
                                        onChange={(e) => setSelectedIntervieweeId(e.target.value)}
                                      >
                                        {interview.participants.map((p) => (
                                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button size="small" color="inherit"
                                        onClick={() => { setAssigningInterview(null); setSelectedIntervieweeId(''); }}>
                                        {t('cancel')}
                                      </Button>
                                      <Button size="small" variant="contained"
                                        onClick={() => handleAssignParticipants(interview)}
                                        disabled={loadingAssign || !selectedIntervieweeId}
                                        startIcon={loadingAssign ? <CircularProgress size={14} color="inherit" /> : null}
                                        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}>
                                        {t('interviews.confirm')}
                                      </Button>
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      {t('interviews.detectedParticipants')} {interview.participants.map((p) => p.name).join(', ')}
                                    </Typography>
                                    <Button size="small" variant="outlined"
                                      onClick={() => { setAssigningInterview(interview); setSelectedIntervieweeId(''); }}
                                      sx={{ borderColor: '#F59E0B', color: '#F59E0B', fontSize: '0.7rem' }}>
                                      {t('interviews.assignRoles')}
                                    </Button>
                                  </>
                                )}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Preguntas de entrevista del cargo: aquí se ANOTAN las respuestas
                de este candidato (compartidas por el equipo de la vacante). Las
                preguntas se generan y editan solo en el modal de entrevistas.
                Lo ya anotado sigue accesible aunque el candidato avance. */}
            {hasInterview && (application?.uid || application?.id) && (
              <AtsInterviewQuestions
                applicationId={application.uid || application.id}
                candidateName={application.candidateName || ''}
                open={open}
                inInterviewStage={canSendActions && currentStageModules.includes('interview')}
                canEdit={canEdit}
                questionsKey={`${position?.interviewQuestions?.questions?.length || 0}-${position?.interviewQuestions?.updatedAt?._seconds || 0}`}
              />
            )}

            {/* Notas (manuales + de cambio de etapa) */}
            {(canEdit || timelineNotes.length > 0) && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                    {t('ats.modules.notes')}
                  </Typography>

                  {/* Agregar nota manual */}
                  {canEdit && (
                    <Box sx={{ mb: 1.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={t('ats.notes.placeholder')}
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        inputProps={{ maxLength: 2000 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleAddNote}
                          disabled={!newNoteText.trim() || savingNote}
                          startIcon={savingNote ? <CircularProgress size={12} color="inherit" /> : null}
                          sx={{ fontSize: '0.7rem', backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
                        >
                          {t('ats.notes.add')}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {timelineNotes.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">
                      {t('ats.notes.empty')}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {timelineNotes.map((item) => {
                        const isEditing = editingNote?.key === item.key;
                        const isManual = item.type === 'manual';
                        const noteText = isManual ? item.note.text : item.entry.note;
                        const author = isManual ? item.note.createdBy : item.entry.changedBy;
                        const wasEdited = isManual ? !!item.note.updatedAt : !!item.entry.noteEditedAt;

                        return (
                          <Paper key={item.key} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {isManual ? (
                                  <Chip
                                    label={t('ats.notes.noteChip')}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem', borderColor: '#6A5AF9', color: '#6A5AF9' }}
                                  />
                                ) : (
                                  <>
                                    {item.entry.fromStageName && (
                                      <Chip label={item.entry.fromStageName} size="small" variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }} />
                                    )}
                                    {item.entry.toStageName && (
                                      <>
                                        <Typography variant="caption" color="text.secondary">→</Typography>
                                        <Chip label={item.entry.toStageName} size="small"
                                          sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#6A5AF9', color: '#fff' }} />
                                      </>
                                    )}
                                  </>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatTs(isManual ? item.note.createdAt : item.entry.changedAt)}
                                </Typography>
                                {canEdit && !isEditing && (
                                  <>
                                    <Tooltip title={t('ats.notes.editTooltip')}>
                                      <IconButton
                                        size="small"
                                        onClick={() => setEditingNote({
                                          type: item.type,
                                          key: item.key,
                                          noteId: isManual ? item.note.id : undefined,
                                          index: isManual ? undefined : item.index,
                                          text: noteText || '',
                                        })}
                                        disabled={savingNote}
                                      >
                                        <EditIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    {isManual && (
                                      confirmDeleteNoteId === item.note.id ? (
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteManualNote(item.note.id)}
                                          disabled={savingNote}
                                          sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5 }}
                                        >
                                          {t('ats.notes.confirmDelete')}
                                        </Button>
                                      ) : (
                                        <Tooltip title={t('ats.notes.deleteTooltip')}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setConfirmDeleteNoteId(item.note.id)}
                                            disabled={savingNote}
                                          >
                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )
                                    )}
                                  </>
                                )}
                              </Box>
                            </Box>

                            {isEditing ? (
                              <>
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  value={editingNote.text}
                                  onChange={(e) => setEditingNote((prev) => ({ ...prev, text: e.target.value }))}
                                  inputProps={{ maxLength: 2000 }}
                                  sx={{ mb: 0.5 }}
                                />
                                {!isManual && (
                                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
                                    {t('ats.notes.stageEditHint')}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <Button size="small" color="inherit" onClick={() => setEditingNote(null)} disabled={savingNote} sx={{ fontSize: '0.7rem' }}>
                                    {t('cancel')}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleSaveNoteEdit}
                                    disabled={savingNote || (isManual && !editingNote.text.trim())}
                                    startIcon={savingNote ? <CircularProgress size={12} color="inherit" /> : null}
                                    sx={{ fontSize: '0.7rem', backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
                                  >
                                    {t('ats.notes.save')}
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                                  {noteText}
                                </Typography>
                                {(author || wasEdited) && (
                                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                    {author}{wasEdited ? ` ${t('ats.notes.edited')}` : ''}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Antecedentes en formato común (Chapi o Alto): identidad, score,
                reportes con el color del proveedor y causas curadas */}
            {backgroundAvailable && (
              <AtsBackgroundCheckSection
                check={backgroundCheck}
                canRetry={canEdit && canRetryCheck}
                retrying={retryingCheck}
                onRetry={handleRetryBackgroundCheck}
              />
            )}

            {/* "Volver a consultar" (2026-10-13): consulta ya hecha (terminada o
                sin datos); si falló, sigue el reintento de arriba. Avisa el costo. */}
            {backgroundAvailable && canEdit && backgroundCheck
              && ['finished', 'not_found'].includes(backgroundCheck.status) && (
              <Box sx={{ mt: -1.5, mb: 3 }}>
                {confirmRecheck ? (
                  <Paper elevation={0} sx={{ p: 1.5, backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 1.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      {t('ats.backgroundCheckStage.forceRecheckConfirm', { points: checkPoints })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleRecheckBackground}
                        disabled={recheckingCheck}
                        startIcon={recheckingCheck ? <CircularProgress size={12} color="inherit" /> : null}
                        sx={{ fontSize: '0.7rem', backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
                      >
                        {t('ats.backgroundCheckStage.forceRecheckGo')}
                      </Button>
                      <Button size="small" color="inherit" onClick={() => setConfirmRecheck(false)} disabled={recheckingCheck} sx={{ fontSize: '0.7rem' }}>
                        {t('cancel')}
                      </Button>
                    </Box>
                  </Paper>
                ) : (
                  <Tooltip title={t('ats.backgroundCheckPricing.recheck', { points: checkPoints })} arrow describeChild>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon fontSize="small" />}
                      onClick={() => setConfirmRecheck(true)}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {t('ats.backgroundCheckStage.forceRecheck')}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Bandeja de revisión de antecedentes (F3): estado determinista +
                decisión auditada del consultor. Nada se descarta solo. */}
            {backgroundAvailable && (liveReview || application?.backgroundReview) && (() => {
              const review = liveReview || application.backgroundReview;
              const decision = reviewDecision;
              // Estado según la consulta real (2026-10-13): "Sin observaciones"
              // solo con una consulta terminada; sin consulta, "Sin consultar"
              // con cómo se consulta en esta vacante (antes decía "Sin
              // observaciones" o "Pendiente" sin haberse consultado nada).
              // "Pendiente" queda para la consulta con error o en proceso y la
              // ficha sin RUT, con su motivo.
              const checkStatus = backgroundCheck?.status || null;
              const missingRut = Boolean(applicantData) && !applicantData.rut;
              let statusKey;
              if (decision?.status === 'approved' || decision?.status === 'rejected') statusKey = decision.status;
              else if (review.status === 'requiere_revision') statusKey = 'requiere_revision';
              else if (checkStatus === 'finished') statusKey = 'ok';
              else if (checkStatus === 'error' || checkStatus === 'processing'
                || (review.status === 'pendiente' && missingRut)) statusKey = 'pendiente';
              else statusKey = 'sin_consultar';
              // Mientras se lee la consulta (o la ficha, para saber si falta el
              // RUT) no se sabe qué decir: se espera en vez de mostrar otro estado
              const checkKnown = !showBackgroundCheck || checkLoaded;
              const waitingData = !checkKnown || (review.status === 'pendiente' && loadingApplicant);
              const showSpinner = waitingData && !['approved', 'rejected', 'requiere_revision'].includes(statusKey);
              const bcStage = getAllStages().find(stageHasBackgroundCheck);
              const notCheckedHint = bcStage
                ? t(bcStage.backgroundCheckAuto ? 'ats.backgroundReview.notCheckedAuto' : 'ats.backgroundReview.notCheckedModule', { stage: bcStage.name, points: checkPoints })
                : t('ats.backgroundReview.notCheckedNoModule');
              const chipSx = {
                approved: { backgroundColor: '#DCFCE7', color: '#16A34A' },
                ok: { backgroundColor: '#DCFCE7', color: '#16A34A' },
                rejected: { backgroundColor: '#FEE2E2', color: '#991B1B' },
                requiere_revision: { backgroundColor: '#FEF3C7', color: '#92400E' },
                pendiente: { backgroundColor: '#F3F4F6', color: '#6B7280' },
                sin_consultar: { backgroundColor: '#F3F4F6', color: '#6B7280' },
              }[statusKey] || { backgroundColor: '#F3F4F6', color: '#6B7280' };
              return (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                      {t('ats.backgroundReview.title')}
                    </Typography>

                    {showSpinner ? (
                      <CircularProgress size={14} sx={{ display: 'block', mb: 1 }} />
                    ) : (
                      <Chip
                        label={t(`ats.backgroundReview.status.${statusKey}`)}
                        size="small"
                        sx={{ ...chipSx, fontWeight: 700, mb: 1 }}
                      />
                    )}

                    {!showSpinner && statusKey === 'sin_consultar' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {notCheckedHint}
                      </Typography>
                    )}

                    {!showSpinner && (statusKey === 'requiere_revision' || statusKey === 'pendiente') && (review.reasons || []).map((r, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: statusKey === 'pendiente' ? 'text.secondary' : '#92400E' }}>
                        • {r}
                      </Typography>
                    ))}

                    {decision ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {t(`ats.backgroundReview.decidedAs.${decision.status}`)}
                          {decision.by ? ` — ${decision.by}` : ''}
                          {decision.at ? ` (${new Date(decision.at).toLocaleDateString()})` : ''}
                        </Typography>
                        {decision.note && (
                          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                            "{decision.note}"
                          </Typography>
                        )}
                        <Button size="small" color="inherit" onClick={() => setReviewDecision(null)} sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                          {t('ats.backgroundReview.changeDecision')}
                        </Button>
                      </Box>
                    ) : review.status === 'requiere_revision' ? (
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          maxRows={2}
                          placeholder={t('ats.backgroundReview.notePlaceholder')}
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          InputProps={{ sx: { fontSize: '0.8rem' } }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={savingReview}
                            onClick={() => handleReviewDecision('approved')}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {t('ats.backgroundReview.approve')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={savingReview}
                            onClick={() => handleReviewDecision('rejected')}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {t('ats.backgroundReview.reject')}
                          </Button>
                          <Button
                            size="small"
                            disabled={savingReview}
                            onClick={() => setRequestDocsOpen(true)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {t('ats.backgroundReview.requestDocs')}
                          </Button>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </>
              );
            })()}

            {/* Reglas de captación: marcas del bot + motivo de auto-descarte */}
            {(application.captationMarks?.length > 0 || application.autoDiscarded) && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                    {t('ats.captationRules.marksTitle')}
                  </Typography>
                  {application.autoDiscarded && (
                    <Box sx={{ p: 1.25, mb: 1, backgroundColor: '#FEE2E2', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600 }}>
                        {t('ats.captationRules.autoDiscarded')}
                        {application.discardReason ? ` — ${application.discardReason}` : ''}
                      </Typography>
                    </Box>
                  )}
                  {(application.captationMarks || []).map((m, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: '#92400E', mb: 0.25 }}>
                      • {m.label}
                    </Typography>
                  ))}
                </Box>
              </>
            )}

            {/* Respuestas filtradoras */}
            {position.filteringQuestions?.length > 0 && application.filteringAnswers?.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                    {t('ats.modules.filteringAnswers')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {position.filteringQuestions.map((q) => {
                      const answerObj = application.filteringAnswers.find((a) => a.questionId === q.id);
                      if (!answerObj) return null;
                      const answer = Array.isArray(answerObj.answer)
                        ? answerObj.answer.map(translateAnswer).join(', ')
                        : translateAnswer(answerObj.answer);
                      return (
                        <Box key={q.id}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                            {q.question}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {answer || '—'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* Respuestas del formulario del portal (masivas, PLAN_MASIVO_PORTAL
                fase 2): estos postulantes no tienen transcript del bot */}
            {Array.isArray(application.captationAnswers) && application.captationAnswers.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
                    {application.source === 'import'
                      ? t('ats.import.answersTitle', { origin: application.importOrigin || '—' })
                      : t('ats.captation.portalAnswers.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {application.captationAnswers.map((item) => {
                      const isFile = String(item.key || '').startsWith('doc_');
                      const label = t(`ats.captation.portalAnswers.labels.${item.key}`, { defaultValue: item.question || item.key });
                      return (
                        <Box key={item.key}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                            {label}
                          </Typography>
                          {isFile ? (
                            <Button size="small" href={item.answer} target="_blank" rel="noopener noreferrer" sx={{ px: 0, minWidth: 0 }}>
                              {t('ats.captation.portalAnswers.viewFile')}
                            </Button>
                          ) : (
                            <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
                              {item.answer || '—'}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* Conversación del bot de captación (vacantes masivas) */}
            {botConversation && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Box
                    onClick={() => setBotConversationOpen((v) => !v)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('ats.captation.drawer.title')}
                      {botConversation.channel === 'whatsapp' ? ' · WhatsApp' : ''}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ userSelect: 'none' }}>
                      {botConversationOpen ? t('ats.captation.drawer.hide') : t('ats.captation.drawer.show')}
                    </Typography>
                  </Box>
                  {botConversationOpen && (
                    <Box
                      sx={{
                        maxHeight: 360, overflowY: 'auto', p: 1.5, borderRadius: 2,
                        bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200',
                        display: 'flex', flexDirection: 'column', gap: 0.75,
                      }}
                    >
                      {(botConversation.transcript || []).map((m, i) => (
                        <Box
                          key={i}
                          sx={{
                            alignSelf: m.role === 'bot' ? 'flex-start' : 'flex-end',
                            maxWidth: '85%',
                            px: 1.5, py: 0.75, borderRadius: 2,
                            bgcolor: m.role === 'bot' ? 'white' : 'primary.main',
                            color: m.role === 'bot' ? 'text.primary' : 'white',
                            border: m.role === 'bot' ? '1px solid' : 'none',
                            borderColor: 'grey.200',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {m.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Documentos solicitados */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('ats.documents.sectionTitle')}
                </Typography>
                {canEdit && (
                  <Button
                    size="small"
                    startIcon={<UploadFileIcon fontSize="small" />}
                    onClick={() => setRequestDocsOpen(true)}
                  >
                    {t('ats.documents.requestButton')}
                  </Button>
                )}
              </Box>
              {!docRequest || !(docRequest.items || []).length ? (
                <Typography variant="body2" color="text.secondary">
                  {t('ats.documents.none')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(docRequest.items || []).map((it) => (
                    <Box
                      key={it.docId}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {it.label}
                          {it.required ? ' *' : ''}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={it.status === 'uploaded' ? 'success.main' : 'text.secondary'}
                        >
                          {it.status === 'uploaded' ? t('ats.documents.uploaded') : t('ats.documents.pending')}
                        </Typography>
                      </Box>
                      {it.status === 'uploaded' && it.fileUrl && (
                        <Button
                          size="small"
                          href={it.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<DownloadIcon fontSize="small" />}
                          sx={{ flexShrink: 0 }}
                        >
                          {t('ats.documents.download')}
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Info postulación */}
            <Divider sx={{ mb: 2 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                {t('ats.modules.applicationInfo')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('ats.detail.source')}: <strong>{sourceLabel}</strong>
                  {/* Postulación desde la página de una feria (PLAN_FERIAS) */}
                  {application.fairName && (
                    <> · <strong>{t('ats.fairs.origin', { name: application.fairName })}</strong></>
                  )}
                </Typography>
                {application.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.detail.appliedOn')}: <strong>{formatTs(application.createdAt)}</strong>
                  </Typography>
                )}
              </Box>
            </Box>

          </Box>
        </Box>
      </Drawer>

      {/* Modales de resultado */}
      {resultModuleId === 'disc_test' && (
        <DiscReportModal
          open={!!resultTest}
          onClose={() => { setResultTest(null); setResultModuleId(null); }}
          test={resultTest}
        />
      )}
      {resultModuleId === 'reasoning_test' && (
        <ReasoningTestResultModal
          open={!!resultTest}
          onClose={() => { setResultTest(null); setResultModuleId(null); }}
          test={resultTest}
        />
      )}
      {resultModuleId === 'personal_skills' && (
        <PersonalHabilitiesTestResultModal
          open={!!resultTest}
          onClose={() => { setResultTest(null); setResultModuleId(null); }}
          test={resultTest}
        />
      )}
      {resultModuleId === 'sales_assessment' && (
        <SalesAssessmentReportModal
          open={!!resultTest}
          onClose={() => { setResultTest(null); setResultModuleId(null); }}
          test={resultTest}
        />
      )}

      <AtsDiscardDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        application={application}
        rejectionTemplate={rejectionTemplate}
        autoSendDefault={autoSendDefault}
        positionName={positionName}
        orgName={orgName}
        onConfirm={(opts) => {
          setDiscardOpen(false);
          // El kanban decide qué sigue: pasar al siguiente de la etapa (recorrido) o cerrar
          if (onDiscard) onDiscard(application, opts);
          else onClose();
        }}
      />

      {/* Con la solicitud vigente (lo pedido en esta misma apertura incluido), y
          al enviar se actualiza también la tarjeta del kanban (chip "Docs"):
          sin eso, la próxima apertura partía de la solicitud anterior */}
      <AtsRequestDocumentsDialog
        open={requestDocsOpen}
        onClose={() => setRequestDocsOpen(false)}
        application={application ? { ...application, documentRequest: docRequest } : application}
        applicantPhone={applicantData?.phone || null}
        onSent={(items) => {
          const next = { ...(docRequest || {}), items };
          setDocRequest(next);
          if (onApplicationChange && application) {
            onApplicationChange(application.uid || application.id, { documentRequest: next });
          }
        }}
      />

      {/* Envío de tests desde el drawer: mismo modal que el pipeline, con el
          candidato del drawer preseleccionado. Solo módulos de la etapa actual. */}
      <AtsTestSendDialog
        open={!!sendModuleId}
        onClose={() => { setSendModuleId(null); setSendResults(null); }}
        moduleId={sendModuleId}
        moduleConfig={sendModuleId ? MODULE_CONFIG[sendModuleId] : null}
        applications={application ? [application] : []}
        positionId={positionId}
        positionCompetencies={position?.competencias}
        onSendBulk={handleSendBulk}
        sending={sendingTest}
        sendResults={sendResults}
        initialView="create"
        initialSelectedIds={application ? [application.uid || application.id] : []}
      />

      <AtsInterviewDialog
        open={interviewDialogOpen}
        onClose={() => { setInterviewDialogOpen(false); fetchInterviews(); }}
        applications={application ? [application] : []}
        stage={currentStage}
        positionId={positionId}
        position={position}
        onPositionChange={onPositionChange}
      />

      <AtsPsycholaboralDialog
        open={psychoDialogOpen}
        onClose={() => { setPsychoDialogOpen(false); fetchPsicoReport(); }}
        position={position}
        positionId={positionId}
        applications={application ? [application] : []}
      />
    </>
  );
};

export default AtsApplicationDrawer;